import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { localizationService } from '@shared-services/localization-service';
import { Div } from 'client/widget-components/basicComponents';
import Text, {
    TextDomTag,
} from 'client/widget-components/basicComponents/Text';
import { PRODUCT_ONE_TIME_PAYMENT_PLAN_ID } from 'client/widget-components/ecom/consts';
import { ProductPaymentPlan } from 'client/widget-components/ecom/stores/types';
import React, { FC } from 'react';
import { PriceOptionsStyles } from './productPriceOptionsTypes';

interface PriceOptionLabelProps {
    option: ProductPaymentPlan;
    styles?: PriceOptionsStyles;
    /**
     * Replaces the plan price (a pay-what-you-want amount). `null` hides the
     * price, frequency and expiry until there's an amount.
     */
    displayedPrice?: string | null;
}

const { str } = localizationService;

export const ProductPriceOptionsItem: FC<PriceOptionLabelProps> = ({
    option,
    styles,
    displayedPrice,
}) => {
    const {
        id,
        name,
        tag_line,
        frequency,
        expiration_count,
        interval,
        plan_displayed_price,
    } = option;
    const expirationInterval =
        expiration_count && interval && expiration_count * interval;

    return (
        <>
            <Text
                tag={TextDomTag.h4}
                data-grab='price-name'
                styles={[nameDefaultStyles, styles?.name]}
            >
                {name}
            </Text>

            {tag_line && (
                <Text
                    tag={TextDomTag.h6}
                    data-grab='price-tagLine'
                    styles={[tagLineDefaultStyles, styles?.tagLine]}
                >
                    {tag_line}
                </Text>
            )}

            {displayedPrice !== null && (
                <Div>
                    <Text
                        tag={TextDomTag.h4}
                        data-grab='price-and-frequency'
                        styles={[
                            priceAndFrequencyDefaultStyles,
                            styles?.priceAndFrequency,
                        ]}
                    >
                        {displayedPrice ?? plan_displayed_price}
                        {frequency && (
                            <>
                                {' '}
                                /{' '}
                                {interval && interval > 1 ? (
                                    <>
                                        {interval}{' '}
                                        {str(
                                            `ui.runtimessr.productPriceOptions.frequencyPlural.${frequency.toLowerCase()}`
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {str(
                                            `ui.runtimessr.productPriceOptions.frequency.${frequency.toLowerCase()}`
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Text>

                    {id !== PRODUCT_ONE_TIME_PAYMENT_PLAN_ID && (
                        <Text
                            tag={TextDomTag.h6}
                            data-grab='expiration'
                            styles={[
                                expirationDefaultStyles,
                                styles?.expiration,
                            ]}
                        >
                            {expirationInterval && frequency ? (
                                <>
                                    {str(
                                        'ui.runtimessr.productPriceOptions.expiresAfter'
                                    )}{' '}
                                    {expirationInterval}{' '}
                                    {str(
                                        `ui.runtimessr.productPriceOptions.frequency${
                                            expirationInterval > 1
                                                ? 'Plural'
                                                : ''
                                        }.${frequency.toLowerCase()}`
                                    )}
                                </>
                            ) : (
                                str(
                                    'ui.runtimessr.productPriceOptions.autoRenew'
                                )
                            )}
                        </Text>
                    )}
                    {!!option.tax_percentage && (
                        <Div styles={varDefaultStyles} data-auto='price-vat'>
                            <Text data-auto='vat-msg'>
                                {str('ui.runtimessr.productPrice.omnibus.vat', {
                                    vatPercentage:
                                        option.tax_percentage.toString(),
                                })}
                            </Text>
                            <Text data-auto='vat-desc'>
                                {str(
                                    'ui.runtimessr.productPrice.omnibus.total.price.might.change'
                                )}
                            </Text>
                        </Div>
                    )}
                </Div>
            )}
        </>
    );
};

const nameDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        paddingBlockEnd: 4,
        margin: 0,
    },
};

const tagLineDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        paddingBlockEnd: 8,
        margin: 0,
    },
};

const priceAndFrequencyDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        paddingInlineEnd: 8,
        margin: 0,
        display: 'inline',
    },
};

const expirationDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        margin: 0,
        display: 'inline',
    },
};

const varDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        fontSize: '13px',
        margin: '0',
        color: '#939393',
        lineHeight: '15px',
    },
};
